import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import WhatsAppButton from "@/components/WhatsAppButton";
import AuthModal from "@/components/AuthModal";
import UserTypeModal from "@/components/UserTypeModal";
import { FAQSection } from "@/components/landing/FAQSection";
import { BlogSection } from "@/components/landing/BlogSection";
import { CTASection } from "@/components/landing/CTASection";
import { Footer } from "@/components/landing/Footer";
import { CheckoutModal } from "@/components/landing/CheckoutModal";
import { 
  Leaf, 
  Heart, 
  Shield, 
  Sparkles, 
  Star, 
  ChevronDown,
  Package,
  Truck,
  CreditCard,
  Bug,
  Cat,
  User,
  LogIn,
  Menu,
  X,
  Globe,
  FlaskConical,
  Cross,
  Camera,
  ImagePlus,
  ArrowLeftRight,
  Store
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login, loading: authLoading } = useAuth();
  const { t, setShowModal, language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stock, setStock] = useState({ stock: 35, available: true });
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" or "mercadopago"
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [contactMethod, setContactMethod] = useState("email"); // "email" or "phone"
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [shippingMethod, setShippingMethod] = useState("pickup"); // "pickup" or "delivery"
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userType, setUserType] = useState(null); // "business" or "owner"
  const [showUserTypeModal, setShowUserTypeModal] = useState(true);
  
  // Pricing
  const PRODUCT_PRICE = 500;
  const SHIPPING_PRICE_PER_BOX = 150;
  const shippingCost = shippingMethod === "delivery" ? SHIPPING_PRICE_PER_BOX * quantity : 0;
  const totalPrice = (PRODUCT_PRICE * quantity) + shippingCost;

  // Helper: button label based on stock/loading state
  const getButtonLabel = (label) => {
    if (!stock.available) return t("sold_out");
    if (isLoading) return "...";
    return label;
  };

  const getLanguageLabel = () => {
    if (language === "es") return "Español";
    if (language === "en") return "English";
    return "Français";
  };
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStep, setReviewStep] = useState("code"); // "code" or "form"
  const [reviewCode, setReviewCode] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectedReviewPhoto, setSelectedReviewPhoto] = useState(null);

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setShowUserTypeModal(false);
  };

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
    trackVisit();
    fetchStock();
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackVisit = async () => {
    try {
      // Check if already tracked today (unique visitor per day)
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const lastVisitDate = localStorage.getItem('gatofeliz_last_visit');
      
      if (lastVisitDate === today) {
        // Already counted today, don't track again
        return;
      }
      
      // Track the visit
      await axios.post(`${API}/track`, { event_type: "visit" });
      
      // Mark as visited today
      localStorage.setItem('gatofeliz_last_visit', today);
    } catch (error) {
      console.error("Visit tracking failed:", error);
    }
  };

  const trackBuyClick = async () => {
    try {
      await axios.post(`${API}/track`, { event_type: "buy_click" });
    } catch (error) {
      console.error("Buy click tracking failed:", error);
    }
  };

  const fetchStock = async () => {
    try {
      const response = await axios.get(`${API}/stock`);
      setStock(response.data);
    } catch (error) {
      console.error("Stock fetch failed:", error);
    }
  };
  
  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Reviews fetch failed:", error);
    }
  };

  const verifyReviewCode = async () => {
    if (!reviewCode.trim()) {
      toast.error("Ingresa tu código de compra");
      return;
    }
    setReviewLoading(true);
    try {
      await axios.post(`${API}/reviews/verify-code`, { review_code: reviewCode });
      setReviewStep("form");
      toast.success("¡Código válido! Ahora puedes dejar tu reseña");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código inválido");
    } finally {
      setReviewLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;
    
    if (files.length + reviewPhotos.length > 3) {
      toast.error("Máximo 3 fotos permitidas");
      return;
    }
    
    files.forEach(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} no es una imagen válida`);
        return;
      }
      
      // Check file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} es muy grande. Máximo 2MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setReviewPhotos(prev => [...prev, event.target.result]);
        toast.success(`Foto agregada`);
      };
      reader.onerror = () => {
        toast.error(`Error al cargar ${file.name}`);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setReviewPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const submitReview = async () => {
    if (!reviewName.trim() || !reviewComment.trim()) {
      toast.error("Completa todos los campos");
      return;
    }
    setReviewLoading(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        review_code: reviewCode,
        customer_name: reviewName,
        rating: reviewRating,
        comment: reviewComment,
        photos: reviewPhotos.length > 0 ? reviewPhotos : null
      });
      toast.success("¡Gracias por tu reseña!");
      setShowReviewModal(false);
      setReviewStep("code");
      setReviewCode("");
      setReviewName("");
      setReviewRating(5);
      setReviewComment("");
      setReviewPhotos([]);
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar reseña");
    } finally {
      setReviewLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [productRes, faqRes, blogRes] = await Promise.all([
        axios.get(`${API}/product`),
        axios.get(`${API}/faq`),
        axios.get(`${API}/blog`)
      ]);
      setProduct(productRes.data);
      setFaqs(faqRes.data);
      setBlogPosts(blogRes.data);
    } catch (error) {
      console.error("Data fetch failed:", error);
    }
  };

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const status = urlParams.get("status");

    if (sessionId && status === "success") {
      setCheckingPayment(true);
      try {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 5;
        
        const pollStatus = async () => {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            toast.success("¡Pago exitoso! Gracias por tu compra.", {
              description: `Orden #${response.data.order_id?.slice(0, 8)}`,
              duration: 6000
            });
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000);
          } else {
            toast.info("Tu pago está siendo procesado", {
              description: "Recibirás confirmación pronto."
            });
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        };
        
        await pollStatus();
      } catch (error) {
        console.error("Payment status check failed:", error);
      } finally {
        setCheckingPayment(false);
      }
    } else if (status === "cancelled") {
      toast.error("Pago cancelado", {
        description: "Puedes intentar de nuevo cuando quieras."
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Open payment method selection modal
  const openPaymentModal = () => {
    if (!stock.available) {
      toast.error("Producto agotado", {
        description: "Por favor contáctanos por WhatsApp para más información."
      });
      return;
    }
    if (quantity > stock.stock) {
      toast.error("Stock insuficiente", {
        description: `Solo hay ${stock.stock} packs disponibles.`
      });
      return;
    }
    setShowPaymentModal(true);
  };

  const handleCheckout = async (method = paymentMethod) => {
    if (!stock.available) {
      toast.error("Producto agotado", {
        description: "Por favor contáctanos por WhatsApp para más información."
      });
      return;
    }
    if (quantity > stock.stock) {
      toast.error("Stock insuficiente", {
        description: `Solo hay ${stock.stock} packs disponibles.`
      });
      return;
    }
    
    // Validate phone is provided
    if (!customerPhone.trim()) {
      toast.error("Teléfono requerido", {
        description: "Por favor ingresa tu número de teléfono."
      });
      return;
    }
    
    trackBuyClick();
    setIsLoading(true);
    setShowPaymentModal(false);
    
    try {
      // Select endpoint based on payment method
      let endpoint;
      if (method === "mercadopago") {
        endpoint = `${BACKEND_URL}/api/checkout/mercadopago`;
      } else if (method === "paypal") {
        endpoint = `${BACKEND_URL}/api/checkout/paypal`;
      } else {
        endpoint = `${BACKEND_URL}/api/checkout/create`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          quantity: quantity,
          contact_method: "phone",
          customer_phone: customerPhone,
          customer_name: customerName.trim() || null,
          shipping_method: shippingMethod,
          shipping_cost: shippingCost,
          total_amount: totalPrice
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorDetail = `Error ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.detail || errorDetail;
        } catch (e) {
          errorDetail = errorText || errorDetail;
        }
        throw new Error(errorDetail);
      }
      
      const data = await response.json();
      
      // Handle different response formats
      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data && data.approval_url) {
        // PayPal returns approval_url
        window.location.href = data.approval_url;
      } else {
        throw new Error("No se recibió URL de checkout");
      }
    } catch (error) {
      setIsLoading(false);
      
      // More specific error messages
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        toast.error("Error de conexión", {
          description: "No se pudo conectar. Por favor intenta de nuevo."
        });
      } else if (!navigator.onLine) {
        toast.error("Sin conexión", {
          description: "Verifica tu conexión a internet."
        });
      } else {
        const errorMsg = error.message || "Error desconocido";
        toast.error("Error al procesar", {
          description: `${errorMsg}. Contáctanos por WhatsApp.`
        });
      }
    }
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const navHeight = 64;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - navHeight,
          behavior: 'smooth'
        });
      }
    }, 350);
  };

  return (
    <div className="min-h-screen bg-bone">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#AFD29B] backdrop-blur-md border-b border-[#AFD29B]/20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center h-16 md:justify-between">
            {/* Mobile Layout: Logo centered, menu right */}
            <div className="flex md:hidden items-center justify-center w-full relative">
              <img src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/eeist4qj_LOGO%20FONDO%20TRANSPARENTE.png" alt="GatoFeliz Logo" className="h-12 w-auto translate-x-[20px] brightness-0 invert" />
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 absolute right-[-40px]"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
            
            {/* Desktop: Logo left */}
            <div className="hidden md:flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/eeist4qj_LOGO%20FONDO%20TRANSPARENTE.png" alt="GatoFeliz Logo" className="h-16 w-auto brightness-0 invert" />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection("que-es-catnip")} 
                className="font-fredoka text-lg text-white hover:text-marigold transition-colors"
                data-testid="nav-producto"
              >
                {t("nav_producto")}
              </button>
              <button 
                onClick={() => scrollToSection("beneficios")} 
                className="font-fredoka text-lg text-white hover:text-marigold transition-colors"
                data-testid="nav-beneficios"
              >
                {t("nav_beneficios")}
              </button>
              <button 
                onClick={() => scrollToSection("faq")} 
                className="font-fredoka text-lg text-white hover:text-marigold transition-colors"
                data-testid="nav-faq"
              >
                {t("nav_faq")}
              </button>
              <button 
                onClick={() => setShowModal(true)} 
                className="font-fredoka text-lg text-white hover:text-marigold transition-colors flex items-center gap-1"
                data-testid="nav-lang"
              >
                <Globe className="w-4 h-4" />
                {language?.toUpperCase() || "ES"}
              </button>
            </div>
            
            {/* Desktop Auth & Buy Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => setUserType(userType === "business" ? "owner" : "business")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/40 text-white/90 hover:bg-white/20 transition-all font-nunito text-sm"
                data-testid="switch-profile-btn"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                {userType === "business" ? "Soy dueño" : "Soy negocio"}
              </button>
              {!authLoading && (
                user ? (
                  <Button 
                    onClick={() => navigate("/dashboard")}
                    variant="ghost"
                    className="text-white hover:bg-white/20 font-fredoka rounded-full px-4"
                    data-testid="nav-cuenta-btn"
                  >
                    <User className="w-5 h-5 mr-2" />
                    {t("nav_cuenta")}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => setShowAuthModal(true)}
                    variant="ghost"
                    className="text-white hover:bg-white/20 font-fredoka rounded-full px-4"
                    data-testid="nav-login-btn"
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    {t("nav_login")}
                  </Button>
                )
              )}
              {userType === "business" && (
                <Button 
                  onClick={openPaymentModal}
                  disabled={isLoading}
                  className="bg-mexican hover:bg-mexican/90 text-white font-fredoka font-bold rounded-full px-6"
                  data-testid="nav-comprar-btn"
                >
                  {isLoading ? "..." : t("nav_comprar")}
                </Button>
              )}
            </div>
            
            {/* Mobile: Placeholder for right side to balance the layout */}
            <div className="md:hidden w-10"></div>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#AFD29B] border-t border-white/20"
            >
              <div className="px-4 py-4 space-y-3">
                <button 
                  onClick={() => scrollToSection("que-es-catnip")} 
                  className="block w-full text-left font-fredoka text-lg text-white hover:text-marigold py-2"
                >
                  {t("nav_producto")}
                </button>
                <button 
                  onClick={() => scrollToSection("beneficios")} 
                  className="block w-full text-left font-fredoka text-lg text-white hover:text-marigold py-2"
                >
                  {t("nav_beneficios")}
                </button>
                <button 
                  onClick={() => scrollToSection("faq")} 
                  className="block w-full text-left font-fredoka text-lg text-white hover:text-marigold py-2"
                >
                  {t("nav_faq")}
                </button>
                <button 
                  onClick={() => { setShowModal(true); setMobileMenuOpen(false); }} 
                  className="block w-full text-left font-fredoka text-lg text-white hover:text-marigold py-2 flex items-center gap-2"
                >
                  <Globe className="w-5 h-5" />
                  {getLanguageLabel()}
                </button>
                <div className="border-t border-white/20 pt-3 space-y-2">
                  <button
                    onClick={() => { setUserType(userType === "business" ? "owner" : "business"); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-2 font-fredoka text-lg text-white hover:text-marigold py-2"
                    data-testid="switch-profile-mobile-btn"
                  >
                    <ArrowLeftRight className="w-5 h-5" />
                    {userType === "business" ? "Cambiar a Dueño" : "Cambiar a Negocio"}
                  </button>
                  {!authLoading && (
                    user ? (
                      <Button 
                        onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                        variant="ghost"
                        className="w-full justify-start text-white hover:bg-white/20 font-fredoka"
                      >
                        <User className="w-5 h-5 mr-2" />
                        {t("nav_cuenta")}
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                        variant="ghost"
                        className="w-full justify-start text-white hover:bg-white/20 font-fredoka"
                      >
                        <LogIn className="w-5 h-5 mr-2" />
                        {t("nav_login")}
                      </Button>
                    )
                  )}
                  {userType === "business" && (
                    <Button 
                      onClick={() => { openPaymentModal(); setMobileMenuOpen(false); }}
                      disabled={isLoading}
                      className="w-full bg-mexican hover:bg-mexican/90 text-white font-fredoka font-bold rounded-full"
                    >
                      {isLoading ? "..." : `${t("nav_comprar")} - $500`}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-12 md:pt-32 md:pb-24 px-4 md:px-8 relative overflow-hidden min-h-[85vh] md:min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/88t3jddp_FOTO%20BANER%20GATOFELIZ.jpg"
            alt="" 
            className="w-full h-full object-cover object-center md:object-center scale-110 md:scale-100"
          />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative w-full">
          <div className="max-w-2xl mx-auto">
            <motion.div {...fadeUp} className="text-center">
              <img src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/eeist4qj_LOGO%20FONDO%20TRANSPARENTE.png" alt="GatoFeliz Logo" className="h-48 w-auto mx-auto mb-6 md:mb-8 drop-shadow-lg" />
              <p className="font-nunito text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 md:mb-10 max-w-xl mx-auto drop-shadow-md px-2">
                {t("hero_description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4 sm:px-0">
                {userType === "business" && (
                  <Button 
                    onClick={openPaymentModal}
                    disabled={isLoading || !stock.available}
                    size="lg"
                    className={`${stock.available ? 'bg-mexican hover:bg-mexican/90' : 'bg-gray-400 cursor-not-allowed'} text-white font-fredoka font-bold rounded-full px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg md:text-xl shadow-lg hover:shadow-xl transition-all ${stock.available ? 'hover:-translate-y-1 btn-wiggle' : ''}`}
                    data-testid="hero-comprar-btn"
                  >
                    {getButtonLabel(t("hero_btn_comprar"))}
                  </Button>
                )}
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => scrollToSection("que-es-catnip")}
                  className="border-2 border-white text-white hover:bg-white/20 font-fredoka font-bold rounded-full px-6 sm:px-10 py-5 sm:py-7 text-base sm:text-lg md:text-xl"
                  data-testid="hero-conocer-btn"
                >
                  {t("hero_btn_conocer")}
                </Button>
              </div>
              {userType === "business" && (
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 mt-8 md:mt-10 justify-center">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="font-nunito text-sm sm:text-base text-white drop-shadow-md font-bold">{t("hero_envio")}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <Truck className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  <span className="font-nunito text-sm sm:text-base text-white drop-shadow-md font-bold">{t("hero_envio_mexico")}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 -ml-[10px]">
                  <Shield className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  <span className="font-nunito text-sm sm:text-base text-white drop-shadow-md">{t("hero_pago")}</span>
                </div>
              </div>
              )}
            </motion.div>
          </div>
        </div>
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-agave/50" />
        </motion.div>
      </section>

      {/* What is Catnip Section */}
      <section id="que-es-catnip" className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_gatofeliz-catnip-2/artifacts/d3507f58_SECCION%20CULTIVO.png"
            alt="Cultivo de Catnip" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/40"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <motion.div {...fadeUp} className="text-center mb-12 md:mb-16">
            <h2 className="font-gluten text-2xl sm:text-3xl md:text-4xl text-agave mb-4">
              {t("catnip_section_title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Card 1 - Planta Deshidratada */}
            <motion.div 
              {...fadeUp}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-agave/10"
            >
              <div className="w-16 h-16 bg-agave/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-agave" />
              </div>
              <h3 className="font-gluten text-lg md:text-xl text-agave text-center mb-3">
                {t("catnip_card1_title")}
              </h3>
              <p className="font-nunito text-sm md:text-base text-gray-600 text-center leading-relaxed">
                {t("catnip_card1_desc")}
              </p>
            </motion.div>

            {/* Card 2 - Premio Favorito */}
            <motion.div 
              {...fadeUp}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-agave/10"
            >
              <div className="w-16 h-16 bg-mexican/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cat className="w-8 h-8 text-mexican" />
              </div>
              <h3 className="font-gluten text-lg md:text-xl text-agave text-center mb-3">
                {t("catnip_card2_title")}
              </h3>
              <p className="font-nunito text-sm md:text-base text-gray-600 text-center leading-relaxed">
                {t("catnip_card2_desc")}
              </p>
            </motion.div>

            {/* Card 3 - Sin Aditivos */}
            <motion.div 
              {...fadeUp}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-agave/10"
            >
              <div className="w-16 h-16 bg-agave/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FlaskConical className="w-8 h-8 text-agave" />
              </div>
              <h3 className="font-gluten text-lg md:text-xl text-agave text-center mb-3">
                {t("catnip_card3_title")}
              </h3>
              <p className="font-nunito text-sm md:text-base text-gray-600 text-center leading-relaxed">
                {t("catnip_card3_desc")}
              </p>
            </motion.div>

            {/* Card 4 - No Adictivo */}
            <motion.div 
              {...fadeUp}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-agave/10"
            >
              <div className="w-16 h-16 bg-mexican/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cross className="w-8 h-8 text-mexican" />
              </div>
              <h3 className="font-gluten text-lg md:text-xl text-agave text-center mb-3">
                {t("catnip_card4_title")}
              </h3>
              <p className="font-nunito text-sm md:text-base text-gray-600 text-center leading-relaxed">
                {t("catnip_card4_desc")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Section - Only for business users */}
      {userType === "business" && (
      <section id="producto" className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/fjar5i9v_SECCION%20CULTIVO.png"
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/70"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <motion.div {...fadeUp} className="text-center mb-8 md:mb-12 px-2">
            <h2 className="font-opera-western text-4xl text-agave mb-0">
              {t("product_section_title")}
            </h2>
            <p className="font-opera-western text-6xl text-agave mb-4">
              {t("product_section_brand")}
            </p>
            <p className="font-nunito text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("product_section_description").split(/VETERINARIAS|VETERINARY CLINICS|CLINIQUES VÉTÉRINAIRES|TIENDAS DE MASCOTAS|PET STORES|ANIMALERIES/).map((part, i, arr) => {
                if (i === arr.length - 1) return <span key={`desc-${i}`}>{part}</span>;
                const match = t("product_section_description").match(/VETERINARIAS|VETERINARY CLINICS|CLINIQUES VÉTÉRINAIRES|TIENDAS DE MASCOTAS|PET STORES|ANIMALERIES/g);
                return <span key={`desc-${i}`}>{part}<strong className="font-bold text-foreground">{match && match[i]}</strong></span>;
              })}
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="flex flex-row items-center justify-center gap-3 sm:gap-4 lg:gap-6 px-2">
            {/* Product Info Card */}
            <div className="bg-bone rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-5 shadow-lg w-1/2 max-w-[200px] sm:max-w-[250px] aspect-square flex flex-col justify-center">
              {/* Isometric Box Icon */}
              <div className="flex items-center justify-center mb-1">
                <svg width="28" height="28" viewBox="0 0 100 100" className="text-mexican sm:w-[36px] sm:h-[36px]">
                  <g fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polygon points="50,15 85,35 50,55 15,35" fill="currentColor" fillOpacity="0.1"/>
                    <polygon points="15,35 50,55 50,90 15,70" fill="currentColor" fillOpacity="0.2"/>
                    <polygon points="85,35 50,55 50,90 85,70" fill="currentColor" fillOpacity="0.15"/>
                    <line x1="50" y1="15" x2="85" y2="35"/>
                    <line x1="50" y1="15" x2="15" y2="35"/>
                    <line x1="15" y1="35" x2="50" y2="55"/>
                    <line x1="85" y1="35" x2="50" y2="55"/>
                    <line x1="15" y1="35" x2="15" y2="70"/>
                    <line x1="85" y1="35" x2="85" y2="70"/>
                    <line x1="50" y1="55" x2="50" y2="90"/>
                    <line x1="15" y1="70" x2="50" y2="90"/>
                    <line x1="85" y1="70" x2="50" y2="90"/>
                    <line x1="32.5" y1="25" x2="67.5" y2="45"/>
                  </g>
                </svg>
              </div>
              <p className="font-gluten text-base sm:text-xl lg:text-2xl text-agave mb-0.5 text-center">{t("product_price")}</p>
              <p className="font-nunito text-xs sm:text-base lg:text-lg text-gray-700 font-medium mb-1 sm:mb-2 text-center leading-tight">{t("product_subtitle")}</p>
              
              {/* Quantity Selector */}
              <div className="flex items-center justify-center gap-1 mb-1 sm:mb-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-agave/10 hover:bg-agave/20 flex items-center justify-center font-bold text-agave transition-colors text-xs"
                    disabled={quantity <= 1}
                    data-testid="quantity-minus"
                  >
                    -
                  </button>
                  <span className="font-fredoka text-sm sm:text-base w-5 text-center" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(stock.stock, quantity + 1))}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-agave/10 hover:bg-agave/20 flex items-center justify-center font-bold text-agave transition-colors text-xs"
                    disabled={quantity >= stock.stock}
                    data-testid="quantity-plus"
                  >
                    +
                  </button>
                </div>
              </div>
              
              {/* Total Price */}
              <p className="font-gluten text-xs sm:text-base lg:text-lg text-mexican mb-1 sm:mb-2 text-center">
                ${(500 * quantity).toLocaleString()} MXN
              </p>
              
              <Button 
                onClick={openPaymentModal}
                disabled={isLoading || !stock.available}
                className={`w-full ${stock.available ? 'bg-mexican hover:bg-mexican/90' : 'bg-gray-400 cursor-not-allowed'} text-white font-fredoka font-bold rounded-full px-2 py-1.5 sm:py-2 text-[9px] sm:text-xs lg:text-sm shadow-lg ${stock.available ? 'hover:shadow-xl transition-all hover:-translate-y-1' : ''}`}
                data-testid="product-comprar-btn"
              >
                {getButtonLabel(t("product_btn"))}
              </Button>
            </div>

            {/* Product Image - Clickable to enlarge */}
            <div 
              className="w-1/2 max-w-[200px] sm:max-w-[250px] aspect-square overflow-hidden rounded-2xl sm:rounded-3xl shadow-lg cursor-pointer hover:shadow-2xl transition-all hover:scale-105"
              onClick={() => setShowImageModal(true)}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_gatofeliz-catnip-1/artifacts/hyp5xo1l_ICONO%20STRIPE.png"
                alt="Pack GATOFELIZ - Catnip Mexicano"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
      )}

      {/* Benefits Section */}
      <section id="beneficios" className="py-12 md:py-24 px-4 md:px-8 bg-agave/5">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-8 md:mb-12">
            <h2 className="font-gluten text-2xl sm:text-3xl md:text-4xl text-agave mb-4">
              {t("benefits_title")}
            </h2>
            <p className="font-nunito text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-2">
              {t("benefits_subtitle")}
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {[
              {
                icon: Sparkles,
                titleKey: "benefit_1_title",
                descKey: "benefit_1_desc",
                color: "#E91E63"
              },
              {
                icon: Heart,
                titleKey: "benefit_2_title",
                descKey: "benefit_2_desc",
                color: "#3baf29"
              },
              {
                icon: Cat,
                titleKey: "benefit_3_title",
                descKey: "benefit_3_desc",
                color: "#FFC107"
              },
              {
                icon: Leaf,
                titleKey: "benefit_4_title",
                descKey: "benefit_4_desc",
                color: "#3baf29"
              }
            ].map((benefit, benefitIdx) => (
              <motion.div
                key={benefit.titleKey}
                variants={fadeUp}
              >
                <Card 
                  className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-agave/10 hover:bg-white transition-colors h-full"
                  data-testid={`benefit-card-${benefitIdx}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${benefit.color}15` }}
                  >
                    <benefit.icon className="w-7 h-7" style={{ color: benefit.color }} />
                  </div>
                  <h3 className="font-fredoka font-bold text-lg text-foreground mb-2">
                    {t(benefit.titleKey)}
                  </h3>
                  <p className="font-nunito text-muted-foreground">
                    {t(benefit.descKey)}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Reviews Section - Nuestros Clientes */}
      <section id="resenas" className="py-12 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-8 md:mb-12">
            <h2 className="font-gluten text-2xl sm:text-3xl md:text-4xl text-agave mb-4">
              Nuestros Clientes
            </h2>
            <p className="font-nunito text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Opiniones verificadas de quienes ya probaron GATOFELIZ
            </p>
          </motion.div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {reviews.length > 0 ? (
              reviews.slice(0, 6).map((review, index) => (
                <motion.div
                  key={review.id || index}
                  {...fadeUp}
                  className="bg-bone rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'fill-mexican text-mexican' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  <p className="font-nunito text-gray-700 mb-4 italic">
                    "{review.comment}"
                  </p>
                  
                  {/* Review Photos */}
                  {review.photos && review.photos.length > 0 && (
                    <div className="flex gap-2 mb-4 overflow-x-auto">
                      {review.photos.map((photo, photoIndex) => (
                        <img
                          key={photoIndex}
                          src={photo}
                          alt={`Foto de ${review.customer_name}`}
                          className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                          onClick={() => setSelectedReviewPhoto(photo)}
                        />
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-agave/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-agave" />
                    </div>
                    <div>
                      <p className="font-fredoka font-bold text-agave">{review.customer_name}</p>
                      <p className="font-nunito text-xs text-muted-foreground flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Compra verificada
                        {review.photos && review.photos.length > 0 && (
                          <span className="ml-2 flex items-center gap-1">
                            <Camera className="w-3 h-3" /> {review.photos.length}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <Cat className="w-16 h-16 text-agave/30 mx-auto mb-4" />
                <p className="font-nunito text-muted-foreground">
                  Aún no hay reseñas. ¡Sé el primero en compartir tu experiencia!
                </p>
              </div>
            )}
          </div>

          {/* Leave Review Button */}
          <motion.div {...fadeUp} className="text-center">
            <Button
              onClick={() => setShowReviewModal(true)}
              className="bg-mexican hover:bg-mexican/90 text-white font-fredoka font-bold rounded-full px-8 py-6 text-lg"
              data-testid="leave-review-btn"
            >
              <Star className="w-5 h-5 mr-2" />
              Dejar una Reseña
            </Button>
            <p className="font-nunito text-sm text-muted-foreground mt-3">
              Solo clientes verificados pueden dejar reseñas
            </p>
          </motion.div>
        </div>
      </section>

      <FAQSection t={t} />

      <BlogSection t={t} />

      <CTASection t={t} userType={userType} openPaymentModal={openPaymentModal} isLoading={isLoading} stock={stock} />

      <Footer t={t} />

      {/* Loading overlay for payment check */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-agave border-t-transparent rounded-full mx-auto mb-4" />
            <p className="font-fredoka text-foreground">Verificando pago...</p>
          </div>
        </div>
      )}

      <CheckoutModal
        showPaymentModal={showPaymentModal}
        setShowPaymentModal={setShowPaymentModal}
        shippingMethod={shippingMethod}
        setShippingMethod={setShippingMethod}
        quantity={quantity}
        PRODUCT_PRICE={PRODUCT_PRICE}
        shippingCost={shippingCost}
        totalPrice={totalPrice}
        customerName={customerName}
        setCustomerName={setCustomerName}
        customerPhone={customerPhone}
        setCustomerPhone={setCustomerPhone}
        handleCheckout={handleCheckout}
        isLoading={isLoading}
        t={t}
      />

      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {showImageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setShowImageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-3xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute -top-12 right-0 text-white hover:text-mexican transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <img 
                src="https://customer-assets.emergentagent.com/job_gatofeliz-catnip-1/artifacts/hyp5xo1l_ICONO%20STRIPE.png"
                alt="Pack GATOFELIZ - Catnip Mexicano"
                className="w-full h-full object-contain rounded-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Chat Button */}
      <WhatsAppButton phoneNumber="524423412287" />

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* Review Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowReviewModal(false);
              setReviewStep("code");
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setReviewStep("code");
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-mexican/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-mexican" />
                </div>
                <h3 className="font-gluten text-2xl text-agave mb-2">
                  {reviewStep === "code" ? "Verifica tu compra" : "Escribe tu reseña"}
                </h3>
                <p className="font-nunito text-sm text-muted-foreground">
                  {reviewStep === "code" 
                    ? "Ingresa el código de reseña que recibiste con tu compra" 
                    : "Comparte tu experiencia con GATOFELIZ"}
                </p>
              </div>

              {reviewStep === "code" ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Ej: GF-A1B2C3D4"
                    value={reviewCode}
                    onChange={(e) => setReviewCode(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-agave focus:outline-none font-nunito text-center uppercase tracking-wider"
                  />
                  <Button
                    onClick={verifyReviewCode}
                    disabled={reviewLoading || !reviewCode.trim()}
                    className="w-full bg-agave hover:bg-agave/90 text-white font-fredoka font-bold rounded-xl py-6"
                  >
                    {reviewLoading ? "Verificando..." : "Verificar Código"}
                  </Button>
                  <p className="font-nunito text-xs text-center text-muted-foreground">
                    El código fue enviado a tu WhatsApp después de tu compra
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-1 block">Tu nombre</label>
                    <input
                      type="text"
                      placeholder="Ej: María García"
                      value={reviewName}
                      onChange={(e) => setReviewName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-agave focus:outline-none font-nunito"
                    />
                  </div>
                  
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-2 block">Calificación</label>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setReviewRating(star)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-10 h-10 ${star <= reviewRating ? 'fill-mexican text-mexican' : 'text-gray-300'}`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-1 block">Tu comentario</label>
                    <textarea
                      placeholder="Cuéntanos tu experiencia con GATOFELIZ..."
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-agave focus:outline-none font-nunito resize-none"
                    />
                  </div>
                  
                  {/* Photo Upload */}
                  <div>
                    <label className="font-nunito text-sm text-muted-foreground mb-2 block">
                      📷 Fotos de tu gatito (opcional, máx. 3)
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {reviewPhotos.map((photo, idx) => (
                        <div key={`photo-${idx}-${photo.slice(-8)}`} className="relative">
                          <img
                            src={photo}
                            alt={`Foto ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {reviewPhotos.length < 3 && (
                        <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-agave hover:bg-agave/5 transition-all active:bg-agave/10">
                          <ImagePlus className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Agregar</span>
                          <input
                            type="file"
                            accept="image/*,image/heic,image/heif"
                            capture="environment"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={submitReview}
                    disabled={reviewLoading || !reviewName.trim() || !reviewComment.trim()}
                    className="w-full bg-mexican hover:bg-mexican/90 text-white font-fredoka font-bold rounded-xl py-6"
                  >
                    {reviewLoading ? "Enviando..." : "Enviar Reseña"}
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Viewer Modal */}
      <AnimatePresence>
        {selectedReviewPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
            onClick={() => setSelectedReviewPhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
            >
              <button
                onClick={() => setSelectedReviewPhoto(null)}
                className="absolute -top-12 right-0 text-white hover:text-mexican transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <img 
                src={selectedReviewPhoto}
                alt="Foto de reseña"
                className="max-w-full max-h-[85vh] object-contain rounded-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Type Selection Modal */}
      {showUserTypeModal && (
        <UserTypeModal onSelect={handleUserTypeSelect} />
      )}
    </div>
  );
}
